/* @flow */

import * as React from 'react'
import { type Dispatch } from 'redux'
import { connect } from 'react-redux'
import { useEffect } from 'react'
import memoize from 'memoize-one'
import type { Location, Match, RouterHistory } from 'react-router-dom'
import qs from 'querystring'

import { setBrands } from '../brands/actions'
import WebshopTemplate from './WebshopTemplateV2'
import { fetchBrands } from '../brands/actions'
import { WebshopContext } from './shared'

import {
  useAnonymousWebshopSession,
  usePublicBrand,
  useWebshopNavigationLinks,
  useWebshopV2Totals,
  useWebshopFavorites,
} from './hooks'
import { SessionContext, useUrlState } from '../shared'

import { createSessionContext } from '../template/DomContainer'

type Props = {
  children?: React.Node,
  dispatch: Dispatch<any>,
  history: RouterHistory,
  location: Location,
  match: Match,
}

const WebshopAnonymousSession = ({
  brands,
  brandContext,
  currentBreakpoint,
  children,
  dispatch,
  history,
  location,
  match,
  ...rest
}: Props) => {
  const brandId = match.params.brandId
  const [brand, isFetching, { isInitialized }] = usePublicBrand([brandId])

  // We can either hit this route from a shop URL (/shop/{brandId}) or custom domain (b2b.toms.dk)
  const sessionRequestArgs = React.useMemo(
    () => [brand ? brand.id : null],
    [brand]
  )
  const [
    webshopSession,
    isFetchingWebshopSession,
    { refresh: refreshWebshopSession },
  ] = useAnonymousWebshopSession(sessionRequestArgs, {
    ignore: brand == null,
  })

  // We still have components that are connected to the redux store and depend on brands
  // having being fetched that way. Examples are ProductTable and StockList. To accomodate this
  // while we phase out redux we will set the brands directly in the redux store here
  useEffect(() => {
    if (brand) {
      dispatch(setBrands([brand]))
      // Somebody landed on this page but its not a public webshop, we should redirect
      // to login screen
    } else if (!brand && isInitialized) {
      const redirect = `${window.location.pathname}${location.search}`
      history.push(`/login?${qs.stringify({ redirect: encodeURI(redirect) })}`)
    }
  }, [brand, isInitialized])

  const refreshBrands = React.useCallback(() => {
    dispatch(fetchBrands())
  }, [dispatch])

  const session = React.useMemo(() => {
    return {
      entity: {},
      settings: {},
      user: {},
    }
  }, [webshopSession])

  // Query string
  const { currentQuery: query, onUpdateQueryString } = useUrlState(
    history,
    location,
    '/'
  )

  const isMobile = currentBreakpoint === 'mobile'
  const webshopSettings = brand ? brand.settings.webshop_settings : {}

  /*const [favorites, isFetchingFavorites, { refresh: refreshWebshopFavorites }] =
    useWebshopFavorites(brandId)*/

  // navigation
  const navigationFetchArgs = React.useMemo(() => {
    return [brand ? brand.id : null, webshopSession ? webshopSession.id : null]
  }, [brand, webshopSession])
  const navigationFetchOptions = React.useMemo(() => {
    return {
      ignore: !brand || !webshopSession,
    }
  }, [brand, webshopSession])
  const [
    navigationLinks,
    isFetchingNavigationLinks,
    {
      isInitialized: isNavigationLinksInitialized,
      refresh: refreshNavigationLinks,
    },
  ] = useWebshopNavigationLinks(navigationFetchArgs, navigationFetchOptions)

  const onUpdateWebshopSession = React.useCallback(
    (data, formikProps) => {
      return updateWebshopSession(brandId, webshopSessionId, data).then(
        response => {
          if (formikProps) {
            formikProps.setSubmitting(false)
          }

          if (!response.error) {
            return refreshWebshopSession()
          }

          return response
        }
      )
    },
    // use webshop session ID to prevent callback to refresh every time
    // session updates (can cause infinite loop in cart contact step)
    [brandId]
  )

  // Totals
  const totalsRequestArgs = React.useMemo(() => {
    return [brandId, webshopSession ? webshopSession.id : null]
  }, [brandId, webshopSession])
  const totalsRequestOptions = React.useMemo(() => {
    return {
      ignore: !webshopSession,
    }
  }, [webshopSession])
  const [totals, isFetchingTotals, { refresh: refreshWebshopTotals }] =
    useWebshopV2Totals(totalsRequestArgs, totalsRequestOptions)

  const webshopContext = React.useMemo(() => {
    return {
      availableBrand: {
        retailer_connections: [],
      },
      brand,
      currency: brand ? brand.settings.currency : null,
      deliveryLines: [],
      designPages: [],
      drops: [],
      favorites: {
        products: [],
        total: 0,
      },
      isDesignPagesInitialized: true,
      isMobile,
      isNavigationLinksInitialized,
      navigationLinks,
      onUpdateQueryString,
      overrideDesignPages: () => {},
      shopUrl: `/shop/${brandId}`,
      totals,
      query,
      refreshDesignPages: () => {},
      refreshNavigationLinks,
      refreshWebshopTotals,
      setUseWebshopSessionId: () => {},
      updateWebshopSession: onUpdateWebshopSession,
      webshopDesignSettings: brand ? brand.webshop_design_settings : {},
      webshopSession: {
        ...webshopSession,
        customer: {
          name: 1234,
        },
      },
      webshopSettings,
    }
  }, [
    brand,
    brandId,
    isMobile,
    isNavigationLinksInitialized,
    navigationLinks,
    onUpdateQueryString,
    onUpdateWebshopSession,
    refreshNavigationLinks,
    refreshWebshopTotals,
    query,
    totals,
    webshopSession,
    webshopSettings,
  ])

  if (!webshopSession || !brand) {
    return null
  }

  /*
    Force us to remount the webshoptemplate when the session is changed.
    We do this to e.g. re-fech the navigation when drop changes
  */
  return (
    <SessionContext.Provider
      value={createSessionContext(session, brands, brandContext, refreshBrands)}
    >
      <WebshopContext.Provider value={webshopContext}>
        <WebshopTemplate
          brand={brand}
          history={history}
          location={location}
          match={match}
          {...rest}
        >
          {children}
        </WebshopTemplate>
      </WebshopContext.Provider>
    </SessionContext.Provider>
  )
}

export default connect(state => {
  return {
    brands: state.brands,
    brandContext: state.brandContext,
  }
})(WebshopAnonymousSession)

const renderWithSession = memoize((child, brand, webshopSession) => {
  const ChildComponent = child.props.component
  return props => (
    <ChildComponent brand={brand} webshopSession={webshopSession} {...props} />
  )
})
